import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Publications } from "../../API/Publications";
import { deepClone } from "../../API/utils/deepClone";
import { httpAll } from "../../API/utils/httpAll";
import MultipleSelectAlt from "../../components/MultipleSelectAlt";
import { useManageAccount } from "../../customHooks/useManageAccount";

/*
this component is used in the ManageAccountModalContent component to show the subscriptions of the account.
it uses the useManageAccount hook to get the subscriptions and the available subscription types.
*/

export default function Subscriptions(/*{}: SubscriptionsProps*/) {
  const publicationsAPI = useMemo(() => new Publications(), []);
  const [publications, setPublications] = useState<any>(null);
  const [loadingData, setLoadingData] = useState(false);

  const {
    availableSubscriptionsTypes,
    getSubscriptions,
    subscriptions,
    setSubscriptions,
  } = useManageAccount();
  const getPublications = useCallback(() => {
    setLoadingData(true);
    try {
      const publications: any = {};
      availableSubscriptionsTypes.forEach((element: string | number) => {
        publications[element] = publicationsAPI.get({ type: element as any });
      });
      httpAll(publications).then((response) => {
        let _response = deepClone(response);
        // make a distinction between portfolios and basket.
        if (_response.list) {
          const _portfolios = _response.list.filter(
            (item: any) => item.type === "PORTFOLIO"
          );
          const _basket = _response.list.filter(
            (item: any) => item.type === "BASKET"
          );
          delete _response.list;
          _response.portfolios = _portfolios;
          _response.baskets = _basket;
        }
        setPublications(_response);
        setLoadingData(false);
      });
    } catch (error) {
      alert(error);
      setLoadingData(false);
    }
  }, [availableSubscriptionsTypes, publicationsAPI]);

  const updateSubscriptions = useCallback(
    (values: any[], key: string) => {
      let _temp = deepClone(subscriptions);
      if (!_temp) {
        _temp = {};
      }
      _temp[key] = values;
      setSubscriptions(_temp);
    },
    [setSubscriptions, subscriptions]
  );

  // we need a backup of the starting subscriptions to prevent unchecking some values
  const [backup, setBackup] = useState<any>(null);

  const _getSubscriptions = useCallback(async () => {
    const temp = await getSubscriptions();
    setBackup(temp ? deepClone(temp) : null);
  }, [getSubscriptions]);
  useEffect(() => {
    // on component mount we need to:
    // 1. fetch the publications
    // 2. fetch the subscriptions of the account
    // in order to show the selectors with the correct values.
    getPublications();
    _getSubscriptions();

  }, [_getSubscriptions, getPublications]);

  const pubblicationsSelectors = useMemo(() => {
    const widgets: any[] = [];
    if (publications) {
      const keys = Object.keys(publications);
      keys.forEach((key: string, index: number) => {
        widgets.push(
          <MultipleSelectAlt
            preventUnckeck={true}
            key={index}
            data={publications[key]
              .map((item: any) => ({
                label: item.name,
                value: item.id,
              }))
              .sort((a: any, b: any) =>
                a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
              )}
            values={subscriptions && subscriptions[key] ? subscriptions[key] : []}
            onChangeValues={(e: any) => updateSubscriptions(e, key)}
            onOpen={() => {}}
            label={key}
            backup={backup?backup[key]:[]}
          />
        );
      });
    }
    return widgets;
  }, [backup, publications, subscriptions, updateSubscriptions]);
  return (
    <Box display={"flex"}>
      {loadingData ? (
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          gap={1}
        >
          <CircularProgress color="secondary" />
          <Typography>loading publications...</Typography>
        </Box>
      ) : (
        <Box display={"flex"} gap={2} flex={1} flexDirection={"column"}>
          {pubblicationsSelectors}
        </Box>
      )}
    </Box>
  );
}
